/**
 * Hall sections (A to D). A section is a zone of type 'section' whose name is
 * its letter. A table belongs to the section whose region contains the
 * table's centre; nothing is stored on the table, so moving a table into
 * another region changes its letter with no extra bookkeeping.
 */
import type { Box, Point } from './tableGeometry';

export const SECTION_TYPE = 'section';
export const SECTION_LETTERS = ['A', 'B', 'C', 'D'] as const;

export interface SectionRegion extends Box { letter: string }

interface RoomLike {
    type: string;
    name: string;
    position: Point;
    width: number;
    height: number;
}

/** Section zones, as regions keyed by their letter (the zone name, trimmed and upper-cased). */
export function sectionRegions(rooms: ReadonlyArray<RoomLike>): SectionRegion[] {
    return rooms
        .filter(r => r.type === SECTION_TYPE && r.name.trim() !== '')
        .map(r => ({
            letter: r.name.trim().toUpperCase(),
            x: r.position.x,
            y: r.position.y,
            width: r.width,
            height: r.height,
        }));
}

/**
 * Letter of the section containing the point, or null. Regions are half-open
 * (left/top edge in, right/bottom edge out) so two touching sections never both
 * claim the same point. If regions overlap, the smaller one wins.
 */
export function sectionForPoint(point: Point, regions: ReadonlyArray<SectionRegion>): string | null {
    let best: SectionRegion | null = null;
    for (const r of regions) {
        const inside = point.x >= r.x && point.x < r.x + r.width && point.y >= r.y && point.y < r.y + r.height;
        if (!inside) continue;
        if (!best || r.width * r.height < best.width * best.height) best = r;
    }
    return best ? best.letter : null;
}

/** Section letter for one table, from where its centre sits among all rooms. */
export function sectionLetterForRoom(room: RoomLike, rooms: ReadonlyArray<RoomLike>): string | null {
    return sectionForPoint(
        { x: room.position.x + room.width / 2, y: room.position.y + room.height / 2 },
        sectionRegions(rooms),
    );
}

/** Table counts per section letter (tables outside every section are not counted). */
export function tablesPerSection(rooms: ReadonlyArray<RoomLike>): Record<string, number> {
    const regions = sectionRegions(rooms);
    const counts: Record<string, number> = {};
    for (const r of rooms) {
        if (r.type !== 'table') continue;
        const letter = sectionForPoint({ x: r.position.x + r.width / 2, y: r.position.y + r.height / 2 }, regions);
        if (letter) counts[letter] = (counts[letter] ?? 0) + 1;
    }
    return counts;
}
